// Market bindings -- the data-driven roster (GET /api/v1/bindings).
//
// Replaces the hand-transcribed BINDINGS array: the selector populates from
// client_venue_pairs JOIN venue_pair_map on the backend, auth-scoped to the
// session (operators see all clients; a client login sees only its own
// markets -- scoping is server-side, so there is no roster to leak here).
// Labels come from venue_pair_map.display_label. Venue N+1 = collector +
// DB rows; this file does not change.
import { ApiError } from "./api/client.ts";
import { setDexVenues } from "./lib/venues.ts";

export interface Binding {
  clientId: string;
  venue: string;
  pair: string;
  venueType: "cex" | "dex";
  label: string;
}

interface BindingRow {
  client_id: string;
  venue: string;
  pair: string;
  venue_type: "cex" | "dex";
  label: string;
}

interface BindingsResponse {
  count: number;
  bindings: BindingRow[];
}

export function bindingKey(b: Binding): string {
  return `${b.clientId}:${b.venue}:${b.pair}`;
}

// Fetches the session's markets and, as a single side effect, registers the
// DEX venue set consumed by isDexVenue(). AnalyticsTab gates rendering on
// this call resolving, so the registry is always populated before any panel
// asks.
export async function fetchBindings(): Promise<Binding[]> {
  const res = await fetch("/api/v1/bindings", {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(detail, res.status);
  }
  const data = (await res.json()) as BindingsResponse;
  const rows: Binding[] = data.bindings.map((r) => ({
    clientId: r.client_id,
    venue: r.venue,
    pair: r.pair,
    venueType: r.venue_type,
    label: r.label,
  }));
  setDexVenues(rows.filter((r) => r.venueType === "dex").map((r) => r.venue));
  return rows;
}
